import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { PhoneCall, Clock, UserCheck, Users } from "lucide-react";

function useAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/analytics").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function fmtDuration(s) {
  if (!s) return "0s";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
}

const STATUS_COLORS = {
  qualified: "hsl(160 84% 45%)",
  follow_up: "hsl(38 92% 55%)",
  unqualified: "hsl(340 82% 55%)",
  new: "hsl(217 91% 60%)",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 shadow-sm">
      {label && <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="text-xs font-mono flex items-center gap-2">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const { data, loading } = useAnalytics();

  if (loading) return <div className="text-muted-foreground text-sm py-10 text-center">Loading analytics…</div>;
  if (!data) return <div className="text-muted-foreground text-sm py-10 text-center">No data yet.</div>;

  const kpis = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Analytics</p>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Performance Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Voice ops metrics across all calls and agents.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={PhoneCall} label="Total calls" value={kpis.total_calls || 0} />
        <KpiCard icon={Clock} label="Avg duration" value={fmtDuration(kpis.avg_duration_seconds)} />
        <KpiCard icon={Users} label="Leads" value={kpis.total_leads || 0} />
        <KpiCard icon={UserCheck} label="Qualified" value={kpis.qualified_leads || 0} tone="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold">Calls over time</h3>
              <p className="text-xs text-muted-foreground">Daily call volume (last 14 days)</p>
            </div>
          </div>
          <div className="h-72" data-testid="calls-over-time-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.calls_over_time} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.5)" }} />
                <Bar dataKey="calls" name="Calls" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">Lead status split</h3>
            <p className="text-xs text-muted-foreground">Distribution of captured leads</p>
          </div>
          <div className="h-72" data-testid="lead-status-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.lead_status_breakdown}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="45%"
                  innerRadius={54}
                  outerRadius={84}
                  paddingAngle={2}
                >
                  {data.lead_status_breakdown.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || "hsl(var(--muted-foreground))"} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="square"
                  formatter={(v) => <span className="text-xs text-muted-foreground uppercase tracking-wider">{String(v).replace("_", " ")}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">Average call duration</h3>
            <p className="text-xs text-muted-foreground">Trend in seconds per day</p>
          </div>
          <div className="h-64" data-testid="avg-duration-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.calls_over_time} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="avg_duration" name="Avg sec" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold">Agent performance</h3>
            <p className="text-xs text-muted-foreground">Calls handled and qualified leads by agent</p>
          </div>
          <div className="h-64" data-testid="agent-performance-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.agent_performance} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="agent" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.5)" }} />
                <Legend iconType="square" formatter={(v) => <span className="text-xs text-muted-foreground uppercase tracking-wider">{v}</span>} />
                <Bar dataKey="calls" name="Calls" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="qualified" name="Qualified" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "primary" }) {
  const toneCls = {
    primary: "bg-primary/10 border-primary/25 text-primary",
    emerald: "bg-emerald-500/10 border-emerald-500/25 text-emerald-500",
  }[tone];
  return (
    <Card className="p-4 flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold tracking-tight mt-1">{value}</div>
      </div>
      <div className={`w-8 h-8 flex items-center justify-center rounded-md border ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </div>
    </Card>
  );
}
